<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import StageActionsEditor from './StageActionsEditor.vue';

// Manages the ordered stages of a tramite type. Each stage is persisted individually against the
// nested stages sub-resource (create on first save, update afterwards); reordering persists the new
// order of already-saved stages. This is the live source for the named transition actions a stage
// exposes (edited via StageActionsEditor).
const props = defineProps({
  typeId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();

const STATUS_CATEGORIES = ['unstarted', 'started', 'done', 'canceled'];
const APPLICATION_STATUSES = [
  'draft',
  'submitted',
  'in_review',
  'awaiting_citizen',
  'approved',
  'rejected',
  'completed',
  'cancelled',
];

const stages = ref([]);
const loading = ref(true);
const expandedKey = ref(null);
const savingKey = ref(null);
let tmpCounter = 0;

function rowKey(stage) {
  return stage.id ? `id-${stage.id}` : `tmp-${stage._key}`;
}

function normalize(stage) {
  return {
    ...stage,
    actions: Array.isArray(stage.actions) ? stage.actions : [],
  };
}

onMounted(async () => {
  try {
    const data = await store.dispatch('tramiteTypes/fetchStages', props.typeId);
    stages.value = (data || []).map(normalize);
  } catch {
    useAlert(t('TRAMITE_TYPES.STAGES.SAVE_ERROR'));
  } finally {
    loading.value = false;
  }
});

function toggle(stage) {
  const key = rowKey(stage);
  expandedKey.value = expandedKey.value === key ? null : key;
}

function addStage() {
  tmpCounter += 1;
  const stage = {
    _key: tmpCounter,
    name: '',
    stage_key: '',
    description: '',
    position: stages.value.length,
    status_category: 'unstarted',
    application_status: '',
    sla_hours: null,
    is_initial: stages.value.length === 0,
    is_terminal: false,
    is_updatable: true,
    doc_upload_required: false,
    requires_signature: false,
    color: '#6366f1',
    actions: [],
  };
  stages.value = [...stages.value, stage];
  expandedKey.value = rowKey(stage);
}

function buildPayload(stage) {
  // Drop client-only / server-managed keys the stage strong-params don't accept.
  const {
    id,
    _key,
    tramite_type_id,
    created_at,
    updated_at,
    ...attrs
  } = stage;
  return attrs;
}

async function saveStage(stage, index) {
  savingKey.value = rowKey(stage);
  try {
    const payload = buildPayload(stage);
    let saved;
    if (stage.id) {
      saved = await store.dispatch('tramiteTypes/updateStage', {
        typeId: props.typeId,
        stageId: stage.id,
        ...payload,
      });
    } else {
      saved = await store.dispatch('tramiteTypes/createStage', {
        typeId: props.typeId,
        ...payload,
      });
    }
    stages.value.splice(index, 1, normalize(saved));
    expandedKey.value = rowKey(saved);
    useAlert(t('TRAMITE_TYPES.STAGES.SAVE_SUCCESS'));
  } catch (error) {
    const message =
      error.response?.data?.error || t('TRAMITE_TYPES.STAGES.SAVE_ERROR');
    useAlert(message);
  } finally {
    savingKey.value = null;
  }
}

async function deleteStage(stage, index) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TRAMITE_TYPES.STAGES.DELETE_CONFIRM', { name: stage.name || stage.stage_key }))) {
    return;
  }
  try {
    if (stage.id) {
      await store.dispatch('tramiteTypes/deleteStage', {
        typeId: props.typeId,
        stageId: stage.id,
      });
    }
    stages.value.splice(index, 1);
    useAlert(t('TRAMITE_TYPES.STAGES.DELETE_SUCCESS'));
  } catch (error) {
    const message =
      error.response?.data?.error || t('TRAMITE_TYPES.STAGES.SAVE_ERROR');
    useAlert(message);
  }
}

async function persistOrder() {
  const ids = stages.value.filter(stage => stage.id).map(stage => stage.id);
  if (ids.length < 2) return;
  try {
    await store.dispatch('tramiteTypes/reorderStages', {
      typeId: props.typeId,
      ids,
    });
  } catch {
    // Order is already reflected locally; surfacing an error here would be noisy.
  }
}

function moveStage(index, direction) {
  const target = index + direction;
  if (target < 0 || target >= stages.value.length) return;
  const next = [...stages.value];
  [next[index], next[target]] = [next[target], next[index]];
  stages.value = next;
  persistOrder();
}
</script>

<template>
  <div class="flex flex-col gap-3 max-w-2xl">
    <div class="flex justify-between items-start">
      <div class="flex flex-col gap-0.5">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITE_TYPES.STAGES.TITLE') }}
        </h2>
        <p class="text-xs text-n-slate-10">
          {{ t('TRAMITE_TYPES.STAGES.HELP') }}
        </p>
      </div>
      <Button
        size="sm"
        icon="i-lucide-plus"
        :label="t('TRAMITE_TYPES.STAGES.ADD')"
        @click="addStage"
      />
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <Spinner />
    </div>

    <p
      v-else-if="!stages.length"
      class="py-6 text-sm text-center text-n-slate-10"
    >
      {{ t('TRAMITE_TYPES.STAGES.EMPTY') }}
    </p>

    <div v-else class="flex flex-col gap-2">
      <div
        v-for="(stage, index) in stages"
        :key="rowKey(stage)"
        class="rounded-lg border border-n-weak"
      >
        <!-- Row header -->
        <div
          class="flex justify-between items-center p-3 cursor-pointer bg-n-solid-2 hover:bg-n-alpha-1"
          @click="toggle(stage)"
        >
          <div class="flex gap-2 items-center min-w-0">
            <span
              class="w-2.5 h-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: stage.color || '#6366f1' }"
            />
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ stage.name || t('TRAMITE_TYPES.STAGES.NEW_STAGE') }}
            </span>
            <span class="font-mono text-xs text-n-slate-10">
              {{ stage.stage_key }}
            </span>
          </div>
          <div class="flex gap-1 items-center shrink-0" @click.stop>
            <Button
              v-tooltip.top="t('TRAMITE_TYPES.STAGES.MOVE_UP')"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-chevron-up"
              :disabled="index === 0"
              @click="moveStage(index, -1)"
            />
            <Button
              v-tooltip.top="t('TRAMITE_TYPES.STAGES.MOVE_DOWN')"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-chevron-down"
              :disabled="index === stages.length - 1"
              @click="moveStage(index, 1)"
            />
            <Button
              v-tooltip.top="t('TRAMITE_TYPES.STAGES.DELETE_STAGE')"
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-trash-2"
              @click="deleteStage(stage, index)"
            />
          </div>
        </div>

        <!-- Row body -->
        <div
          v-if="expandedKey === rowKey(stage)"
          class="flex flex-col gap-3 p-3 border-t border-n-weak"
        >
          <div class="flex gap-3">
            <div class="flex flex-col flex-1 gap-1">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('TRAMITE_TYPES.STAGES.FIELDS.NAME') }}
              </label>
              <input
                v-model="stage.name"
                type="text"
                class="px-2 h-8 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
            </div>
            <div class="flex flex-col flex-1 gap-1">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('TRAMITE_TYPES.STAGES.FIELDS.KEY') }}
              </label>
              <input
                v-model="stage.stage_key"
                type="text"
                class="px-2 h-8 font-mono text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('TRAMITE_TYPES.STAGES.FIELDS.DESCRIPTION') }}
            </label>
            <input
              v-model="stage.description"
              type="text"
              class="px-2 h-8 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
          </div>

          <div class="flex flex-wrap gap-3">
            <div class="flex flex-col flex-1 gap-1 min-w-40">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('TRAMITE_TYPES.STAGES.FIELDS.STATUS_CATEGORY') }}
              </label>
              <select
                v-model="stage.status_category"
                class="px-2 h-8 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              >
                <option v-for="key in STATUS_CATEGORIES" :key="key" :value="key">
                  {{ t(`TRAMITE_TYPES.STAGES.STATUS_CATEGORY_OPTIONS.${key}`) }}
                </option>
              </select>
            </div>
            <div class="flex flex-col flex-1 gap-1 min-w-40">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('TRAMITE_TYPES.STAGES.FIELDS.APPLICATION_STATUS') }}
              </label>
              <select
                v-model="stage.application_status"
                class="px-2 h-8 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              >
                <option value="">—</option>
                <option v-for="key in APPLICATION_STATUSES" :key="key" :value="key">
                  {{ t(`TRAMITE_TYPES.STAGES.APPLICATION_STATUS_OPTIONS.${key}`) }}
                </option>
              </select>
            </div>
            <div class="flex flex-col gap-1 w-24">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('TRAMITE_TYPES.STAGES.FIELDS.SLA_HOURS') }}
              </label>
              <input
                v-model.number="stage.sla_hours"
                type="number"
                min="0"
                class="px-2 h-8 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
            </div>
            <div class="flex flex-col gap-1 w-16">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('TRAMITE_TYPES.STAGES.FIELDS.COLOR') }}
              </label>
              <input
                v-model="stage.color"
                type="color"
                class="p-0.5 w-full h-8 rounded-md border cursor-pointer border-n-weak bg-n-alpha-black2"
              />
            </div>
          </div>

          <div class="flex flex-wrap gap-x-4 gap-y-2">
            <label class="flex gap-1.5 items-center text-xs cursor-pointer text-n-slate-11">
              <input v-model="stage.is_initial" type="checkbox" />
              {{ t('TRAMITE_TYPES.STAGES.FIELDS.IS_INITIAL') }}
            </label>
            <label class="flex gap-1.5 items-center text-xs cursor-pointer text-n-slate-11">
              <input v-model="stage.is_terminal" type="checkbox" />
              {{ t('TRAMITE_TYPES.STAGES.FIELDS.IS_TERMINAL') }}
            </label>
            <label class="flex gap-1.5 items-center text-xs cursor-pointer text-n-slate-11">
              <input v-model="stage.is_updatable" type="checkbox" />
              {{ t('TRAMITE_TYPES.STAGES.FIELDS.IS_UPDATABLE') }}
            </label>
            <label class="flex gap-1.5 items-center text-xs cursor-pointer text-n-slate-11">
              <input v-model="stage.doc_upload_required" type="checkbox" />
              {{ t('TRAMITE_TYPES.STAGES.FIELDS.DOC_UPLOAD_REQUIRED') }}
            </label>
            <label class="flex gap-1.5 items-center text-xs cursor-pointer text-n-slate-11">
              <input v-model="stage.requires_signature" type="checkbox" />
              {{ t('TRAMITE_TYPES.STAGES.FIELDS.REQUIRES_SIGNATURE') }}
            </label>
          </div>

          <div class="pt-1 border-t border-n-weak/60">
            <StageActionsEditor v-model="stage.actions" :stages="stages" />
          </div>

          <div class="flex justify-end">
            <Button
              size="sm"
              :is-loading="savingKey === rowKey(stage)"
              icon="i-lucide-save"
              :label="t('TRAMITE_TYPES.STAGES.SAVE_STAGE')"
              @click="saveStage(stage, index)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
